import * as Sentry from "@sentry/react";

/**
 * Initialise Sentry if a DSN is provided via VITE_SENTRY_DSN. If the env
 * var is missing (local dev, PR previews without the secret, etc.)
 * this is a silent no-op so nothing breaks.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || "dev",
    // Conservative sampling — crank up after launch if we need more data
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,    // no replays by default
    replaysOnErrorSampleRate: 1.0,  // but always capture replays when an error fires
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Don't ship Supabase auth errors to Sentry — they're expected and
    // noisy (invalid email, wrong password, etc. during signup).
    ignoreErrors: [
      /Invalid login credentials/i,
      /Email not confirmed/i,
      /User already registered/i,
      /Auth session missing/i,
      /Failed to fetch dynamically imported module/i, // handled by lazyWithRetry
    ],
  });
}

/** Report an error to Sentry if initialised; otherwise log to console. */
export function reportError(err: unknown, context?: Record<string, unknown>) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(err, { extra: context });
  } else {
    console.error("[error]", err, context);
  }
}

export { Sentry };
